import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from 'tss-react/mui';
import UploadPhotoButton from "./uploadPhoto";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-mui";
import LockIcon from "@mui/icons-material/Lock";
import {
  Grid,
  Paper,
  Button,
  LinearProgress,
  InputAdornment,
} from "@mui/material";
import {isEqual} from "lodash";
import Avatar from '@mui/material/Avatar';
import { initialsOf } from "./ContactAvatar";

const useStyles = makeStyles()((theme) => ({
  card: {
    margin: "0 !important",
    borderRadius: 12,
    overflow: "hidden",
  },
  // Who this is: photo, name and e-mail, with the photo button on the right.
  identity: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2.5),
    padding: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(2),
      padding: theme.spacing(2),
    },
  },
  avatarProfile: {
    "& img":{
      objectFit: "contain !important"
    },
    width: 88,
    height: 88,
    fontSize: 32,
    fontWeight: 400,
    color: "#fff",
    backgroundColor: theme.palette.primary.light,
    [theme.breakpoints.down("sm")]: {
      width: 64,
      height: 64,
      fontSize: 24,
    },
  },
  // Wide enough for a name: on a phone the photo button goes underneath.
  identityText: {
    flex: "1 1 160px",
    minWidth: 0,
  },
  name: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  email: {
    fontSize: 13.5,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  photoHint: {
    marginTop: 4,
    fontSize: 12,
    textAlign: "center",
    color: theme.palette.text.disabled,
  },
  fields: {
    padding: theme.spacing(3),
    // An empty field shows a light hint, with its label above it.
    "& .MuiInputBase-input::placeholder": {
      fontSize: "0.85em",
      color: theme.palette.text.disabled,
      opacity: 1,
    },
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
  },
  updateButton: {
    textTransform: "none",
    minWidth: 128,
    color: "#fff",
    backgroundColor: theme.palette.primary.light,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      filter: "brightness(0.93)",
      boxShadow: "none",
    },
  },
}));

const UserDetails = (props) => {
    const { classes } = useStyles();
    const {
      session,
      user,
      setUser,
      updateUserProfile,
      getUserProfileData,
    } = props;
    const { username, firstName, lastName, email, profileImage, preferredDevice } = user;
    const [base64File, setBase64File] = useState(null);
    const [formChanged, setFormChanged] = useState(false);
    const [recentlyUpdated, setRecentlyUpdated] = useState(false);
    // eslint-disable-next-line
    const [imageHash, setImageHash] = useState(Date.now());
    const [photo, setPhoto] = useState(
      `https://${session.lastServer}4.admin.my-cloudline.net:9003/${profileImage}?${imageHash}`
    );
    

    const handlePhoto = (file) => {
      // convert and set the base64 version for update later
      toBase64(file);

      // convert and set the url version for preview
      var url = URL.createObjectURL(file);
      setPhoto(url+`#${imageHash}`);
    };

    const handleValidation = (values) => {
      const errors = {};
      
      if (!values.firstName) {
        errors.firstName = "First Name Required";
      }

      if (!values.lastName) {
        errors.lastName = "Last Name Required";
      }
      
      return errors;
    };

    const toBase64 = async file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          // use a regex to remove data url part
          let res = reader.result
              .replace("data:", "")
              .replace(/^.+,/, "");
          setBase64File(res);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (values, { setSubmitting }) => {

        let data = {
          token: session.token,
          server: session.lastServer,
          userProfile: {
            ...user,
            firstName: values.firstName,
            lastName: values.lastName,
            profileImage: base64File,
            timeZone: user.timeZone,
            preferredDevice: preferredDevice,
            preferredDeviceNumbers: {
              mobile: user.preferredDeviceNumbers.mobile,
              custom: user.preferredDeviceNumbers.custom,
            },
            voicemail: {
              active: user.voicemail.active,
            },
            fax: {
              email: user.fax.email,
              active: user.fax.active,
              format: user.fax.format,
            },
          },
        };

        try {
          await updateUserProfile(data);

          setSubmitting(false);

          try {
            await getUserProfileData(
              {
                token: session.token,
                server: session.lastServer,
              },setUser
            );

            setPhoto(
              `https://${
                session.lastServer
              }4.admin.my-cloudline.net:9003/${profileImage}?${Date.now()}`
            );

            setRecentlyUpdated(true);
          } catch (error) {
            setSubmitting(false);
            console.error("error with refetch",error);
          }
        } catch (error) {
          setSubmitting(false);
          console.error("error with updating profile",error);
        }
    };

    const handleFormChange = (e)=>{
      if (!isEqual(user[e.target.name], e.target.value)) {
        setRecentlyUpdated(false);
        setFormChanged(true);
      } else {
        setRecentlyUpdated(true);
        setFormChanged(false);
      }
    }

    return (
      <Formik
        initialValues={{
          username: username,
          email: email,
          firstName: firstName,
          lastName: lastName,
        }}
        validate={handleValidation}
        onSubmit={(values, setSubmitting) =>
          handleSubmit(values, setSubmitting)
        }
      >
        {({ submitForm, isSubmitting }) => (
          <Form
            onSubmit={(e) => e.preventDefault()}
            onChange={handleFormChange}
          >
            <Paper className={classes.card}>
              <div className={classes.identity}>
                <Avatar className={classes.avatarProfile} src={photo}>
                  {initialsOf(`${firstName || ""} ${lastName || ""}`)}
                </Avatar>
                <div className={classes.identityText}>
                  <div className={classes.name}>{`${firstName || ""} ${lastName || ""}`.trim() || username}</div>
                  <div className={classes.email}>{email || username}</div>
                </div>
                <div>
                  <UploadPhotoButton
                    handlePhoto={handlePhoto}
                  />
                  <div className={classes.photoHint}>PNG, up to 1 MB</div>
                </div>
              </div>
              <Grid container className={classes.fields} spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    variant="outlined"
                    component={TextField}
                    fullWidth
                    size="small"
                    name="firstName"
                    type="text"
                    label="First name"
                    placeholder="Add first name"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    variant="outlined"
                    component={TextField}
                    fullWidth
                    size="small"
                    name="lastName"
                    type="text"
                    label="Last name"
                    placeholder="Add last name"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    variant="outlined"
                    component={TextField}
                    fullWidth
                    size="small"
                    disabled
                    name="username"
                    type="text"
                    label="Username"
                    placeholder="Not set"
                    slotProps={{ inputLabel: { shrink: true }, input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <LockIcon color="disabled" fontSize="small" />
                        </InputAdornment>
                      ),
                    } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    variant="outlined"
                    component={TextField}
                    disabled
                    fullWidth
                    size="small"
                    name="email"
                    type="text"
                    label="E-mail"
                    placeholder="Not set"
                    slotProps={{ inputLabel: { shrink: true }, input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <LockIcon color="disabled" fontSize="small" />
                        </InputAdornment>
                      ),
                    } }}
                  />
                </Grid>
              </Grid>
              {isSubmitting && <LinearProgress />}
              <div className={classes.footer}>
                <Button
                  variant="contained"
                  disabled={isSubmitting || !formChanged || recentlyUpdated}
                  onClick={submitForm}
                  className={classes.updateButton}
                >
                  {isSubmitting ? "Updating…" : "Update"}
                </Button>
              </div>
            </Paper>
          </Form>
        )}
      </Formik>
    );
};

UserDetails.propTypes = {
  user: PropTypes.object.isRequired,
  session: PropTypes.object.isRequired,
  setUser: PropTypes.func.isRequired,
  updateUserProfile: PropTypes.func.isRequired,
  getUserProfileData: PropTypes.func.isRequired,
};

export default UserDetails;
